/**
 * Which panel the media modal shows on its right-hand side.
 *
 * The value is **persisted** (backend `user.settings.momentModalDefaultTab`) and **linkable**
 * (`?tab=` on a media URL), so it arrives from outside the type system and has to be validated
 * on the way in — but only once, here.
 *
 * This module has no imports on purpose. Stores, pages and components all need the enum, and it
 * previously lived in a page's context file, which made a store reach into `@pages` for it.
 */

export const MODAL_TABS = ['visual', 'scenes', 'transcript', 'comments'] as const;

export type ModalTab = (typeof MODAL_TABS)[number];

/**
 * The single reader for a tab that came from a URL or a stored setting. `undefined` means "not
 * one of ours" — which callers must treat as "no preference expressed", never as a tab.
 *
 * It exists because this check used to be written out by hand at four call sites. Adding
 * `scenes` to the union updated three of them; the fourth kept silently dropping the new value,
 * and the Scenes tab looked like a button that did nothing. Widening the union must not depend
 * on someone finding every copy.
 */
export function parseModalTab(value: unknown): ModalTab | undefined {
  return MODAL_TABS.includes(value as ModalTab) ? (value as ModalTab) : undefined;
}
